import { ROUTES, servicePath, articlePath } from "./routes";

export const SITE_ORIGIN = "https://socialdestiny.ro";

const SERVICE_ENTRIES = [
  {
    slug: "social-media-management",
    nameRo: "Social Media Management",
    nameEn: "Social Media Management",
    descRo: "Content premium, strategie și community.",
    descEn: "Premium content, strategy and community.",
  },
  {
    slug: "ads",
    nameRo: "Campanii ADS",
    nameEn: "ADS Campaigns",
    descRo: "Meta, Google, TikTok, LinkedIn, YouTube.",
    descEn: "Meta, Google, TikTok, LinkedIn, YouTube.",
  },
  {
    slug: "website",
    nameRo: "Website",
    nameEn: "Websites",
    descRo: "eCommerce, WordPress, custom & SEO.",
    descEn: "eCommerce, WordPress, custom & SEO.",
  },
  {
    slug: "consultanta",
    nameRo: "Consultanță Marketing",
    nameEn: "Marketing Consulting",
    descRo: "Strategie 1-la-1 pentru founderi și echipe.",
    descEn: "1-on-1 strategy for founders and teams.",
  },
];

function truncate(text, max = 160) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

export function absoluteCanonical(path, origin = SITE_ORIGIN) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${p === "/" ? "/" : p.replace(/\/$/, "") || "/"}`;
}

function cleanPhone(phone) {
  if (!phone) return undefined;
  const trimmed = String(phone).trim();
  return trimmed || undefined;
}

function sameAsLinks(contactInfo) {
  return [
    contactInfo?.socialInstagramUrl,
    contactInfo?.socialFacebookUrl,
    contactInfo?.socialLinkedinUrl,
    contactInfo?.socialTiktokUrl,
  ]
    .map((url) => (typeof url === "string" ? url.trim() : ""))
    .filter(Boolean);
}

function postalAddressFromContact(t, lang) {
  const location = t?.contact?.location_value || (lang === "en" ? "Bucharest, Romania" : "București, România");
  const parts = String(location).split(",").map((p) => p.trim());
  const locality = parts[0] || (lang === "en" ? "Bucharest" : "București");
  const countryName = parts[1] || (lang === "en" ? "Romania" : "România");
  return {
    "@type": "PostalAddress",
    addressLocality: locality,
    addressCountry: countryName === "România" || countryName === "Romania" ? "RO" : countryName,
  };
}

function openingHoursSpecification() {
  return [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  ];
}

function contactPoints(contactInfo, lang) {
  const phone = cleanPhone(contactInfo?.phone);
  const email = contactInfo?.email?.trim();
  if (!phone && !email) return undefined;

  return [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: phone,
      email,
      availableLanguage: lang === "en" ? ["English", "Romanian"] : ["Romanian", "English"],
      areaServed: "RO",
    },
  ];
}

function buildOrganization(contactInfo, lang, t, origin) {
  const name = contactInfo?.companyName?.trim() || "Social Destiny";
  const description =
    lang === "en"
      ? "Predictable marketing agency: social media, paid ads, websites and strategy."
      : "Agenție de marketing predictibilă: social media, campanii ads, website și strategie.";

  const org = {
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name,
    url: `${origin}/`,
    logo: {
      "@type": "ImageObject",
      url: `${origin}/logo_sd.png`,
    },
    image: `${origin}/logo_sd.png`,
    description,
    email: contactInfo?.email?.trim() || undefined,
    telephone: cleanPhone(contactInfo?.phone),
    address: postalAddressFromContact(t, lang),
    contactPoint: contactPoints(contactInfo, lang),
    sameAs: sameAsLinks(contactInfo),
  };

  if (contactInfo?.companyCui?.trim()) {
    org.taxID = contactInfo.companyCui.trim();
    org.identifier = contactInfo.companyCui.trim();
  }

  return org;
}

function buildWebSite(origin) {
  return {
    "@type": "WebSite",
    "@id": `${origin}/#website`,
    url: `${origin}/`,
    name: "Social Destiny",
    publisher: { "@id": `${origin}/#organization` },
    inLanguage: ["ro-RO", "en-US"],
  };
}

function buildProfessionalService(contactInfo, lang, t, origin, pageSeo) {
  return {
    "@type": "ProfessionalService",
    "@id": `${origin}/#business`,
    name: contactInfo?.companyName?.trim() || "Social Destiny",
    url: `${origin}/`,
    image: `${origin}/logo_sd.png`,
    description: pageSeo?.description,
    telephone: cleanPhone(contactInfo?.phone),
    email: contactInfo?.email?.trim() || undefined,
    priceRange: "$$",
    address: postalAddressFromContact(t, lang),
    openingHoursSpecification: openingHoursSpecification(),
    areaServed: { "@type": "Country", name: "Romania" },
    parentOrganization: { "@id": `${origin}/#organization` },
    sameAs: sameAsLinks(contactInfo),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: lang === "en" ? "Marketing services" : "Servicii marketing",
      itemListElement: SERVICE_ENTRIES.map((entry, index) => ({
        "@type": "Offer",
        position: index + 1,
        itemOffered: {
          "@type": "Service",
          name: lang === "en" ? entry.nameEn : entry.nameRo,
          description: lang === "en" ? entry.descEn : entry.descRo,
          url: absoluteCanonical(servicePath(entry.slug), origin),
          provider: { "@id": `${origin}/#organization` },
        },
      })),
    },
  };
}

export function buildBreadcrumbs(pathname, lang, t, serviceData, origin = SITE_ORIGIN) {
  const path = pathname.replace(/\/$/, "") || "/";
  const homeLabel = lang === "en" ? "Home" : "Acasă";
  const items = [{ name: homeLabel, path: ROUTES.home }];

  if (path === ROUTES.home || path === "/") return items;

  if (path === ROUTES.services) {
    items.push({ name: t.nav?.services || "Servicii", path: ROUTES.services });
    return items;
  }

  if (path.startsWith(`${ROUTES.services}/`) && serviceData) {
    items.push({ name: t.nav?.services || "Servicii", path: ROUTES.services });
    items.push({ name: serviceData.title, path });
    return items;
  }

  if (path === ROUTES.portfolio) {
    items.push({ name: t.nav?.portfolio || "Portofoliu", path: ROUTES.portfolio });
    return items;
  }

  if (path === ROUTES.about) {
    items.push({ name: t.nav?.about || "Despre", path: ROUTES.about });
    return items;
  }

  if (path === ROUTES.contact) {
    items.push({ name: t.nav?.contact || "Contact", path: ROUTES.contact });
    return items;
  }

  if (path === ROUTES.articles) {
    items.push({ name: t.nav?.articles || "Articole", path: ROUTES.articles });
    return items;
  }

  if (path.startsWith(`${ROUTES.articles}/`)) {
    items.push({ name: t.nav?.articles || "Articole", path: ROUTES.articles });
    const slug = path.split("/").pop();
    const article = (Array.isArray(t?.articles) ? t.articles : []).find(
      (a) => a && a.status !== "draft" && String(a.slug || "").toLowerCase() === String(slug || "").toLowerCase()
    );
    items.push({ name: article?.title || slug, path });
    return items;
  }

  return items;
}

function webPageType(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";
  if (path === ROUTES.contact) return "ContactPage";
  if (path === ROUTES.about) return "AboutPage";
  if (path === ROUTES.portfolio) return "CollectionPage";
  if (path === ROUTES.services || path.startsWith(`${ROUTES.services}/`)) return "WebPage";
  return "WebPage";
}

function buildWebPage(pathname, lang, pageSeo, origin) {
  const canonical = absoluteCanonical(pageSeo.path || pathname, origin);
  const type = webPageType(pathname);

  return {
    "@type": type,
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: pageSeo.title,
    description: pageSeo.description,
    isPartOf: { "@id": `${origin}/#website` },
    about: { "@id": `${origin}/#organization` },
    inLanguage: lang === "en" ? "en-US" : "ro-RO",
  };
}

function buildServicesItemList(lang, origin) {
  return {
    "@type": "ItemList",
    name: lang === "en" ? "Services" : "Servicii",
    itemListElement: SERVICE_ENTRIES.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: lang === "en" ? entry.nameEn : entry.nameRo,
      url: absoluteCanonical(servicePath(entry.slug), origin),
    })),
  };
}

function buildPortfolioItemList(t, origin) {
  const items = Array.isArray(t?.portfolio?.items) ? t.portfolio.items : [];
  if (items.length === 0) return null;

  return {
    "@type": "ItemList",
    name: t?.portfolio?.title || "Portofoliu",
    itemListElement: items.slice(0, 12).map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "CreativeWork",
        name: item.name,
        description: item.service,
        image: item.image?.startsWith("http") ? item.image : undefined,
      },
    })),
  };
}

/**
 * Build full schema.org @graph for the current page.
 */
export function buildSchemaGraph({
  pathname,
  lang,
  t,
  serviceData,
  contactInfo,
  pageSeo,
  origin = SITE_ORIGIN,
  articles = [],
}) {
  const path = pathname.replace(/\/$/, "") || "/";
  const canonical = absoluteCanonical(pageSeo.path || pathname, origin);
  const graph = [];

  graph.push(buildOrganization(contactInfo, lang, t, origin));
  graph.push(buildWebSite(origin));
  graph.push(buildProfessionalService(contactInfo, lang, t, origin, pageSeo));
  graph.push(buildWebPage(pathname, lang, pageSeo, origin));

  const crumbs = buildBreadcrumbs(pathname, lang, t, serviceData, origin);
  if (crumbs.length > 1) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: absoluteCanonical(c.path, origin),
      })),
    });
  }

  if (path === ROUTES.services) {
    graph.push(buildServicesItemList(lang, origin));
  }

  if (serviceData && path.startsWith(`${ROUTES.services}/`)) {
    graph.push({
      "@type": "Service",
      "@id": `${canonical}#service`,
      name: serviceData.title,
      description: truncate(serviceData.lead || serviceData.intro_body, 300),
      provider: { "@id": `${origin}/#organization` },
      areaServed: { "@type": "Country", name: "Romania" },
      url: canonical,
      serviceType: serviceData.title,
    });
  }

  if (path === ROUTES.portfolio) {
    const portfolioList = buildPortfolioItemList(t, origin);
    if (portfolioList) graph.push(portfolioList);
  }

  if (path === ROUTES.articles && Array.isArray(articles) && articles.length) {
    graph.push({
      "@type": "ItemList",
      name: t?.articles_page?.title || (lang === "en" ? "Articles" : "Articole"),
      itemListElement: articles.slice(0, 20).map((a, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: a.title,
        url: absoluteCanonical(articlePath(a.slug), origin),
      })),
    });
  }

  if (path.startsWith(`${ROUTES.articles}/`) && Array.isArray(articles)) {
    const slug = path.split("/").pop();
    const article = articles.find((a) => a.slug === slug);
    if (article) {
      const image = article.coverUrl
        ? article.coverUrl.startsWith("http")
          ? article.coverUrl
          : `${origin}${article.coverUrl.startsWith("/") ? "" : "/"}${article.coverUrl}`
        : undefined;
      graph.push({
        "@type": "BlogPosting",
        "@id": `${canonical}#article`,
        headline: article.title,
        description: truncate(article.seoDescription || article.excerpt || article.title, 300),
        datePublished: article.publishedAt || undefined,
        author: {
          "@type": "Organization",
          name: article.author || "Social Destiny",
          url: origin,
        },
        publisher: { "@id": `${origin}/#organization` },
        mainEntityOfPage: { "@id": `${canonical}#webpage` },
        image: image ? [image] : undefined,
        url: canonical,
        inLanguage: lang === "en" ? "en-US" : "ro-RO",
      });
    }
  }

  return { "@context": "https://schema.org", "@graph": graph };
}
